import React, { useCallback, useState } from "react";
import {
  IconButton,
  Stack,
  TextField,
  Button,
  Typography,
  Card,
  Box,
  Link,
  useMediaQuery,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import Divider from "@mui/material/Divider";
import LoadingButton from "@mui/lab/LoadingButton";
import { alpha, useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
// Remove this import: import { bgGradient } from "../../theme/css";
import Logo from "../../components/logo";
import Iconify from "../../components/iconify";
import AuroraBackground from "../../components/AuroraBackground"; // Add this import
import { useNavigate } from "react-router-dom";
import useAuth from "src/hooks/useAuth";
import AuthService from "src/services/Auth.service";
import toast from "react-hot-toast";
import { logEvent } from "firebase/analytics";
import { analytics } from "src/config/firebase.config";

const SignupView = () => {
  const theme = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = useCallback(
    async (formData, setSubmitting, reset) => {
      try {
        setSubmitting(true);
        const res = await AuthService.signup(formData);
        if (res.success) {
          logEvent(analytics, "button_click", { button_name: "Sign Up" });
          toast.success(res.message);
          await login(formData.email, formData.password);
        } else {
          toast.error(res.message);
        }
        reset();
      } catch (error) {
        console.error("Error Signing up:", error);
        toast.error("Failed to Sign up. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [AuthService.signup, login]
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("This field is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      onSubmit(values, setSubmitting, resetForm);
    },
  });

  const renderForm = (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={3}>
        <TextField
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          name="name"
          label="Name"
          helperText={formik.touched.name && formik.errors.name}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              "& fieldset": {
                borderColor: alpha(theme.palette.common.white, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          name="email"
          label="Email address"
          helperText={formik.touched.email && formik.errors.email}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              "& fieldset": {
                borderColor: alpha(theme.palette.common.white, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        <TextField
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              "& fieldset": {
                borderColor: alpha(theme.palette.common.white, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
          name="confirmPassword"
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(10px)",
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              "& fieldset": {
                borderColor: alpha(theme.palette.common.white, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  <Iconify
                    icon={
                      showConfirmPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                    }
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        color="primary"
        disabled={formik.isSubmitting}
        sx={{ my: 3 }}
      >
        {formik.isSubmitting ? <CircularProgress size={24} /> : "Sign up"}
      </LoadingButton>
      <Typography
        variant="subtitle2"
        sx={{
          display: "flex",
          justifyContent: "center",
          opacity: 0.7,
          marginTop: 3,
        }}
      >
        By Signing up you agree to&nbsp;
        <Link href="/terms-of-use" target="_blank" sx={{ cursor: "pointer" }}>
          <Typography
            underline="hover"
            sx={{
              display: "inline",
              color: "secondary.main",
            }}
          >
            Terms & Condition
          </Typography>
        </Link>
      </Typography>
    </form>
  );

  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    // Replace the Box with bgGradient with AuroraBackground
    <AuroraBackground showRadialGradient={true}>
      <Logo
        sx={{
          position: "fixed",
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 10, // Ensure logo stays on top
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-evenly"
        sx={{
          height: "100%",
          width: "100%",
          position: "relative",
          zIndex: 1, // Ensure content stays above background
        }}
      >
        <Card
          sx={{
            p: 5,
            width: 1,
            maxWidth: 500,
            // Glassmorphism effect
            backdropFilter: "blur(15px)",
            backgroundColor:
              isSmUp &&
              (theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.1)
                : alpha(theme.palette.background.paper, 0.2)),
            border:
              isSmUp && `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            borderRadius: isSmUp ? 2 : 0,
            position: "relative",
            overflow: "hidden",
            // Enhanced shadow with multiple layers
            boxShadow:
              isSmUp &&
              `
              0 8px 32px ${alpha(theme.palette.common.black, 0.1)},
              0 4px 16px ${alpha(theme.palette.common.black, 0.05)},
              inset 0 2px 2px ${alpha(theme.palette.common.white, 0.15)}
            `,
            // Subtle gradient overlay
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: `linear-gradient(90deg, 
                transparent 0%, 
                ${alpha(theme.palette.primary.light, 0.4)} 70%, 
                transparent 100%
              )`,
            },
            // Additional frosted glass effect
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                isSmUp &&
                (theme.palette.mode === "dark"
                  ? `linear-gradient(135deg,
                    ${alpha(theme.palette.primary.main, 0.02)} 0%,
                    ${alpha(theme.palette.background.paper, 0.01)} 100%
                  )`
                  : `linear-gradient(135deg,
                    ${alpha(theme.palette.primary.main, 0.03)} 0%,
                    ${alpha(theme.palette.background.paper, 0.04)} 100%
                  )`),
              pointerEvents: "none",
              zIndex: -1,
            },
          }}
        >
          <Typography variant="h3">Sign up for Riple</Typography>

          <Typography variant="body1" sx={{ mt: 2, mb: 5 }}>
            Already have an account?
            <Link
              color="inherit"
              variant="subtitle2"
              sx={{ ml: 0.5, cursor: "pointer" }}
              onClick={() => navigate("/login")}
            >
              Sign in
            </Link>
          </Typography>

          {renderForm}
        </Card>
      </Stack>
    </AuroraBackground>
  );
};

export default SignupView;
